/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Feature Flags Dashboard Page
 * ============================================================================
 *
 * Dashboard for managing global and per-organization feature flags.
 * Shows flag status, rollout percentage, overrides, and provides toggle actions.
 *
 * ============================================================================
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ToggleLeft,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Plus,
  Trash2,
  Settings,
  Filter,
} from 'lucide-react';
import {
  useFeatureFlags,
  useFlagCategories,
  useFeatureFlag,
  useFlagOverrides,
  useToggleFlag,
  useUpdateFlag,
  useCreateOverride,
  useDeleteOverride,
} from '@/hooks/api/useFeatureFlags';
import { FeatureFlag, FlagOverride } from '@/services/superadmin/feature-flags.service';

export function FeatureFlagsPage() {
  const { t } = useTranslation('platform');
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [selectedFlag, setSelectedFlag] = useState<string | null>(null);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideForm, setOverrideForm] = useState({ orgId: '', enabled: true, reason: '' });

  const { data: flags, isLoading, error, refetch } = useFeatureFlags(selectedCategory);
  const { data: categories } = useFlagCategories();
  const { data: flagDetail } = useFeatureFlag(selectedFlag || '');
  const { data: overrides } = useFlagOverrides(selectedFlag || '');
  const toggleMutation = useToggleFlag();
  const updateMutation = useUpdateFlag();
  const createOverrideMutation = useCreateOverride();
  const deleteOverrideMutation = useDeleteOverride();

  const handleToggle = (code: string) => {
    toggleMutation.mutate(code);
  };

  const handleRolloutChange = (code: string, percentage: number) => {
    updateMutation.mutate({ code, request: { rolloutPercentage: percentage } });
  };

  const handleCreateOverride = () => {
    if (selectedFlag && overrideForm.orgId) {
      createOverrideMutation.mutate(
        { code: selectedFlag, request: overrideForm },
        {
          onSuccess: () => {
            setShowOverrideModal(false);
            setOverrideForm({ orgId: '', enabled: true, reason: '' });
          },
        }
      );
    }
  };

  const handleDeleteOverride = (code: string, orgId: string) => {
    deleteOverrideMutation.mutate({ code, orgId });
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="loading-state">
          <RefreshCw className="animate-spin" size={24} />
          <span>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="error-state">
          <AlertTriangle size={24} />
          <span>{t('common.error')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">
            <ToggleLeft className="title-icon" />
            {t('featureFlags.title')}
          </h1>
          <div className="header-actions">
            <button className="btn-secondary" onClick={() => refetch()}>
              <RefreshCw size={16} />
              {t('common.refresh')}
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <Filter size={16} />
          <select
            className="filter-select"
            value={selectedCategory || ''}
            onChange={(e) => setSelectedCategory(e.target.value || undefined)}
          >
            <option value="">{t('featureFlags.allCategories')}</option>
            {categories?.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Flags Table */}
      <div className="section">
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('featureFlags.columns.flag')}</th>
                <th>{t('featureFlags.columns.category')}</th>
                <th>{t('featureFlags.columns.status')}</th>
                <th>{t('featureFlags.columns.rollout')}</th>
                <th>{t('featureFlags.columns.overrides')}</th>
                <th>{t('featureFlags.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {flags?.map((flag) => (
                <tr key={flag.id} className={flag.enabled ? '' : 'row-disabled'}>
                  <td>
                    <div className="flag-cell">
                      <span className="flag-code">{flag.flagCode}</span>
                      <span className="flag-name">{flag.displayName}</span>
                      {flag.description && (
                        <span className="flag-description">{flag.description}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {flag.category && (
                      <span className="category-badge">{flag.category}</span>
                    )}
                  </td>
                  <td>
                    <div className="status-cell">
                      {flag.enabled ? (
                        <>
                          <CheckCircle className="status-icon status-enabled" />
                          <span className="status-text enabled">{t('featureFlags.enabled')}</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="status-icon status-disabled" />
                          <span className="status-text disabled">{t('featureFlags.disabled')}</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="rollout-cell">
                      <div className="rollout-bar">
                        <div
                          className="rollout-fill"
                          style={{ width: `${flag.rolloutPercentage}%` }}
                        />
                      </div>
                      <span className="rollout-value">{flag.rolloutPercentage}%</span>
                    </div>
                  </td>
                  <td>
                    <span className={`override-badge ${flag.overrideCount > 0 ? 'has-overrides' : ''}`}>
                      {flag.overrideCount} {t('featureFlags.overrides')}
                    </span>
                  </td>
                  <td>
                    <div className="actions-cell">
                      <button
                        className={`btn-toggle ${flag.enabled ? 'enabled' : 'disabled'}`}
                        onClick={() => handleToggle(flag.flagCode)}
                        disabled={toggleMutation.isPending}
                        title={flag.enabled ? t('featureFlags.disable') : t('featureFlags.enable')}
                      >
                        <ToggleLeft size={16} />
                      </button>
                      <button
                        className="btn-action"
                        onClick={() => setSelectedFlag(flag.flagCode)}
                        title={t('featureFlags.viewDetails')}
                      >
                        <Settings size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {(!flags || flags.length === 0) && (
                <tr>
                  <td colSpan={6} className="empty-state">
                    {t('featureFlags.noFlags')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Flag Detail Panel */}
      {selectedFlag && flagDetail && (
        <div className="detail-panel">
          <div className="panel-header">
            <h3 className="panel-title">{flagDetail.displayName}</h3>
            <button className="btn-close" onClick={() => setSelectedFlag(null)}>
              <XCircle size={18} />
            </button>
          </div>

          <div className="panel-content">
            <div className="detail-section">
              <label className="detail-label">{t('featureFlags.rolloutPercentage')}</label>
              <div className="rollout-control">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={flagDetail.rolloutPercentage}
                  onChange={(e) => handleRolloutChange(flagDetail.flagCode, Number(e.target.value))}
                  className="rollout-slider"
                />
                <span className="rollout-display">{flagDetail.rolloutPercentage}%</span>
              </div>
            </div>

            <div className="detail-section">
              <div className="section-header">
                <label className="detail-label">{t('featureFlags.organizationOverrides')}</label>
                <button
                  className="btn-add"
                  onClick={() => setShowOverrideModal(true)}
                >
                  <Plus size={14} />
                  {t('featureFlags.addOverride')}
                </button>
              </div>

              <div className="overrides-list">
                {overrides?.map((override) => (
                  <div key={override.id} className="override-item">
                    <div className="override-info">
                      <span className="override-org">{override.orgId}</span>
                      <span className={`override-status ${override.enabled ? 'enabled' : 'disabled'}`}>
                        {override.enabled ? t('featureFlags.enabled') : t('featureFlags.disabled')}
                      </span>
                      {override.reason && (
                        <span className="override-reason">{override.reason}</span>
                      )}
                    </div>
                    <button
                      className="btn-delete"
                      onClick={() => handleDeleteOverride(selectedFlag, override.orgId)}
                      disabled={deleteOverrideMutation.isPending}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                {(!overrides || overrides.length === 0) && (
                  <div className="no-overrides">
                    {t('featureFlags.noOverrides')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Override Modal */}
      {showOverrideModal && (
        <div className="modal-backdrop" onClick={() => setShowOverrideModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">{t('featureFlags.addOverride')}</h3>
            <div className="form-group">
              <label>{t('featureFlags.organizationId')}</label>
              <input
                type="text"
                className="form-input"
                value={overrideForm.orgId}
                onChange={(e) => setOverrideForm({ ...overrideForm, orgId: e.target.value })}
                placeholder="Organization UUID"
              />
            </div>
            <div className="form-group">
              <label>{t('featureFlags.columns.status')}</label>
              <select
                className="form-select"
                value={overrideForm.enabled ? 'true' : 'false'}
                onChange={(e) => setOverrideForm({ ...overrideForm, enabled: e.target.value === 'true' })}
              >
                <option value="true">{t('featureFlags.enabled')}</option>
                <option value="false">{t('featureFlags.disabled')}</option>
              </select>
            </div>
            <div className="form-group">
              <label>{t('featureFlags.reason')}</label>
              <input
                type="text"
                className="form-input"
                value={overrideForm.reason}
                onChange={(e) => setOverrideForm({ ...overrideForm, reason: e.target.value })}
                placeholder={t('featureFlags.reasonPlaceholder')}
              />
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowOverrideModal(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={handleCreateOverride}
                disabled={!overrideForm.orgId || createOverrideMutation.isPending}
              >
                {t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{pageStyles}</style>
    </div>
  );
}

const pageStyles = `
  .page-container {
    padding: 24px;
    max-width: 1400px;
    margin: 0 auto;
  }

  .page-header {
    margin-bottom: 24px;
  }

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: var(--color-text-primary);
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .title-icon {
    color: var(--color-brand);
  }

  .header-actions {
    display: flex;
    gap: 8px;
  }

  .btn-secondary {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border: 1px solid var(--color-border);
    background: var(--color-bg-secondary);
    color: var(--color-text-primary);
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-secondary:hover {
    background: var(--color-bg-hover);
  }

  .btn-primary {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border: none;
    background: var(--color-brand);
    color: white;
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-primary:hover:not(:disabled) {
    opacity: 0.9;
  }

  .btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Filters */
  .filters-section {
    margin-bottom: 24px;
    display: flex;
    gap: 16px;
  }

  .filter-group {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--color-text-secondary);
  }

  .filter-select {
    padding: 8px 12px;
    border: 1px solid var(--color-border);
    background: var(--color-bg-secondary);
    color: var(--color-text-primary);
    border-radius: 6px;
    font-size: 13px;
    min-width: 150px;
  }

  /* Section */
  .section {
    margin-bottom: 32px;
  }

  /* Table */
  .table-container {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    overflow: hidden;
  }

  .data-table {
    width: 100%;
    border-collapse: collapse;
  }

  .data-table th {
    text-align: left;
    padding: 12px 16px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--color-text-secondary);
    background: var(--color-bg-tertiary, #F9FAFB);
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .data-table td {
    padding: 12px 16px;
    font-size: 13px;
    color: var(--color-text-primary);
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .data-table tr:last-child td {
    border-bottom: none;
  }

  .data-table tr:hover {
    background: var(--color-bg-hover);
  }

  .data-table tr.row-disabled {
    opacity: 0.6;
  }

  .flag-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .flag-code {
    font-family: monospace;
    font-size: 12px;
    color: var(--color-text-muted);
  }

  .flag-name {
    font-weight: 500;
  }

  .flag-description {
    font-size: 11px;
    color: var(--color-text-muted);
  }

  .category-badge {
    display: inline-block;
    padding: 2px 8px;
    background: var(--color-bg-tertiary, #F3F4F6);
    border-radius: 4px;
    font-size: 11px;
    text-transform: uppercase;
  }

  .status-cell {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .status-icon {
    width: 16px;
    height: 16px;
  }

  .status-icon.status-enabled { color: var(--color-success, #10B981); }
  .status-icon.status-disabled { color: var(--color-text-muted); }

  .status-text {
    font-size: 12px;
  }

  .status-text.enabled { color: var(--color-success, #10B981); }
  .status-text.disabled { color: var(--color-text-muted); }

  .rollout-cell {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .rollout-bar {
    width: 80px;
    height: 6px;
    background: var(--color-bg-tertiary, #E5E7EB);
    border-radius: 3px;
    overflow: hidden;
  }

  .rollout-fill {
    height: 100%;
    background: var(--color-brand);
    border-radius: 3px;
    transition: width 0.3s ease;
  }

  .rollout-value {
    font-size: 12px;
    font-family: monospace;
    min-width: 40px;
  }

  .override-badge {
    display: inline-block;
    padding: 2px 8px;
    background: var(--color-bg-tertiary, #F3F4F6);
    border-radius: 4px;
    font-size: 11px;
  }

  .override-badge.has-overrides {
    background: var(--color-warning-bg, #FEF3C7);
    color: var(--color-warning, #B45309);
  }

  .actions-cell {
    display: flex;
    gap: 8px;
  }

  .btn-toggle,
  .btn-action {
    width: 28px;
    height: 28px;
    border: 1px solid var(--color-border);
    background: var(--color-bg-secondary);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-toggle:hover:not(:disabled),
  .btn-action:hover:not(:disabled) {
    background: var(--color-bg-hover);
  }

  .btn-toggle:disabled,
  .btn-action:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-toggle.enabled { color: var(--color-success, #10B981); }
  .btn-toggle.disabled { color: var(--color-text-muted); }

  .empty-state {
    text-align: center;
    color: var(--color-text-muted);
    padding: 40px 16px !important;
  }

  /* Detail Panel */
  .detail-panel {
    position: fixed;
    top: 0;
    right: 0;
    width: 400px;
    height: 100vh;
    background: var(--color-bg-secondary);
    border-left: 1px solid var(--color-border);
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
    z-index: 50;
    display: flex;
    flex-direction: column;
  }

  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .panel-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .btn-close {
    background: none;
    border: none;
    color: var(--color-text-secondary);
    cursor: pointer;
    padding: 4px;
  }

  .btn-close:hover {
    color: var(--color-text-primary);
  }

  .panel-content {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
  }

  .detail-section {
    margin-bottom: 24px;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .detail-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-secondary);
    text-transform: uppercase;
    margin-bottom: 8px;
    display: block;
  }

  .rollout-control {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .rollout-slider {
    flex: 1;
    height: 4px;
    -webkit-appearance: none;
    appearance: none;
    background: var(--color-bg-tertiary, #E5E7EB);
    border-radius: 2px;
    cursor: pointer;
  }

  .rollout-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    background: var(--color-brand);
    border-radius: 50%;
    cursor: pointer;
  }

  .rollout-display {
    font-size: 14px;
    font-weight: 600;
    min-width: 45px;
    text-align: right;
  }

  .btn-add {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: var(--color-brand);
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 11px;
    cursor: pointer;
  }

  .btn-add:hover {
    opacity: 0.9;
  }

  .overrides-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .override-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background: var(--color-bg-tertiary, #F9FAFB);
    border-radius: 6px;
  }

  .override-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .override-org {
    font-family: monospace;
    font-size: 12px;
  }

  .override-status {
    font-size: 11px;
  }

  .override-status.enabled { color: var(--color-success, #10B981); }
  .override-status.disabled { color: var(--color-text-muted); }

  .override-reason {
    font-size: 11px;
    color: var(--color-text-muted);
  }

  .btn-delete {
    background: none;
    border: none;
    color: var(--color-error, #DC2626);
    cursor: pointer;
    padding: 4px;
  }

  .btn-delete:hover {
    opacity: 0.7;
  }

  .no-overrides {
    text-align: center;
    color: var(--color-text-muted);
    padding: 24px;
    font-size: 13px;
  }

  /* Modal */
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
  }

  .modal-content {
    background: var(--color-bg-secondary);
    border-radius: 8px;
    padding: 24px;
    width: 100%;
    max-width: 400px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  }

  .modal-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin-bottom: 20px;
  }

  .form-group {
    margin-bottom: 16px;
  }

  .form-group label {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: var(--color-text-secondary);
    margin-bottom: 6px;
  }

  .form-input,
  .form-select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--color-border);
    background: var(--color-bg-primary);
    color: var(--color-text-primary);
    border-radius: 6px;
    font-size: 13px;
  }

  .form-input:focus,
  .form-select:focus {
    outline: none;
    border-color: var(--color-brand);
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 24px;
  }

  /* Loading/Error States */
  .loading-state,
  .error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 60px;
    color: var(--color-text-secondary);
  }

  .error-state {
    color: var(--color-error, #DC2626);
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  @media (max-width: 768px) {
    .page-container {
      padding: 16px;
    }

    .header-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }

    .detail-panel {
      width: 100%;
    }
  }
`;

export default FeatureFlagsPage;
